import React from 'react';
import './AIEnhancedBadge.css';

const AIEnhancedBadge = ({ vulnerability }) => {
    if (!vulnerability || !vulnerability.ai_enhanced) {
        return null;
    }

    const getPriorityClass = (score) => {
        if (score >= 9) return 'critical';
        if (score >= 7) return 'high';
        if (score >= 4) return 'medium';
        return 'low';
    };

    return (
        <div className="ai-enhanced-container">
            <div className="ai-badge">
                <span className="ai-indicator">[AI]</span>
                <span className="ai-label">AI Enhanced</span>
                {vulnerability.ai_providers_used && vulnerability.ai_providers_used.length > 0 && (
                    <span className="ai-providers">
                        ({vulnerability.ai_providers_used.join(', ')})
                    </span>
                )}
            </div>

            <div className="ai-details">
                {/* Exploit Warning */}
                {vulnerability.ai_exploit_available && (
                    <div className="ai-alert exploit-alert">
                        <span className="alert-icon">[!]</span>
                        <div className="alert-content">
                            <strong>Active Exploit Available</strong>
                            <p>This vulnerability has known public exploits. Immediate action required.</p>
                        </div>
                    </div>
                )}

                {/* Patch Availability */}
                {vulnerability.ai_patch_available && (
                    <div className="ai-info patch-info">
                        <span className="info-icon">[i]</span>
                        <strong>Patch Available</strong>
                    </div>
                )}

                {/* Priority Score */}
                {vulnerability.ai_priority_score && (
                    <div className={`priority-badge ${getPriorityClass(vulnerability.ai_priority_score)}`}>
                        <strong>Priority Score:</strong> {vulnerability.ai_priority_score}/10
                    </div>
                )}

                {/* Immediate Actions */}
                {vulnerability.ai_immediate_actions && vulnerability.ai_immediate_actions.length > 0 && (
                    <div className="ai-actions">
                        <strong>Immediate Actions:</strong>
                        <ul>
                            {vulnerability.ai_immediate_actions.slice(0, 3).map((action, index) => (
                                <li key={index}>{action}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Threat Actors */}
                {vulnerability.ai_threat_actors && vulnerability.ai_threat_actors.length > 0 && (
                    <div className="ai-threats">
                        <strong>Threat Actors:</strong>
                        <div className="threat-tags">
                            {vulnerability.ai_threat_actors.slice(0, 5).map((actor, index) => (
                                <span key={index} className="threat-tag">{actor}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Real World Exploits */}
                {vulnerability.ai_real_world_exploits && vulnerability.ai_real_world_exploits.length > 0 && (
                    <div className="ai-exploits">
                        <strong>Real-World Exploitation:</strong>
                        <ul>
                            {vulnerability.ai_real_world_exploits.slice(0, 3).map((exploit, index) => (
                                <li key={index}>{exploit}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Business Impact */}
                {vulnerability.ai_business_impact && (
                    <div className="ai-business-impact">
                        <strong>Business Impact:</strong>
                        <p>{vulnerability.ai_business_impact}</p>
                    </div>
                )}

                {/* Code Examples */}
                {vulnerability.ai_code_examples && vulnerability.ai_code_examples.length > 0 && (
                    <div className="ai-code-examples">
                        <strong>Remediation Code Examples:</strong>
                        {vulnerability.ai_code_examples.slice(0, 2).map((example, index) => (
                            <div key={index} className="code-example">
                                <div className="code-header">
                                    <span className="code-language">{example.language || 'Code'}</span>
                                    {example.description && <span className="code-desc">{example.description}</span>}
                                </div>
                                <pre className="code-block">{example.code}</pre>
                            </div>
                        ))}
                    </div>
                )}

                {/* Enrichment Timestamp */}
                {vulnerability.ai_enriched_at && (
                    <div className="ai-timestamp">
                        <small>AI enriched: {new Date(vulnerability.ai_enriched_at).toLocaleString()}</small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIEnhancedBadge;
